import styled from '@emotion/styled';
import { Button as AntdButton, Icon, Spin, Tooltip } from 'antd';
import * as History from 'history';
import React from 'react';
import { Link } from 'react-router-dom';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

import { hexToRgba } from '../helpers/hexToRgba';

export type ButtonType =
    | 'accept'
    | 'accept2'
    | 'cancel'
    | 'light'
    | 'disabled'
    | 'noBackground'
    | 'white'
    | 'white2'
    | 'withIcon';

export type PaddingSize = 'no-padding' | 'small' | 'medium' | 'large';
export type ButtonWidth = 'normal';

interface LoadingIconProps {
    color: string;
}

const LoadingIcon = styled(Icon)<LoadingIconProps>(props => ({
    position: 'absolute',
    top: '60%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: font.size.medium,
    color: props.color,
}));

interface StyledButtonProps {
    fontSize?: number;
    isfullwidth?: number;
    issmall?: number;
    horizontalPadding?: PaddingSize;
    withshadow?: number;
    buttontype?: ButtonType;
    isPill?: boolean;
    borderColor?: string;
    width?: ButtonWidth;
}

// Because AntdButton is a wrapper around <button /> we can't render it directly
// or we'll pass invalid DOM properties.
const BaseButtonComponent: React.FC<
    { className?: string; style?: React.CSSProperties; testID?: string } & (
        | {
              to: History.LocationDescriptor;
              replace?: boolean;
          }
        | {
              href: string;
              target?: string;
          }
        | {
              onClick?: () => void;
          }
    )
> = props => {
    const { className, style, testID } = props;
    if ('to' in props && props.to) {
        // We should correctly handle react routing without using onClick
        return (
            <Link
                to={props.to}
                replace={props.replace ?? false}
                data-testid={testID}
                className={`ant-btn ${className ?? ''}`}
                style={{
                    ...(style || {}),
                    // We default to 'initial' which will not correctly display an <a> tag.
                    // This is because <a> tags default to 'inline' which won't
                    // include the button's padding in the parent calculations.
                    display: 'inline-block',
                }}
            >
                {props.children}
            </Link>
        );
    } else if ('href' in props && props.href) {
        return (
            <AntdButton
                href={props.href}
                target={props.target}
                data-testid={testID}
                className={className}
                style={{
                    ...(style || {}),
                    // We default to 'initial' which will not correctly display an <a> tag. (see above)
                    // TODO(evanmh): We should explore why we are using 'initial' when a button is
                    // not specified as 'centered' and consider a better default.
                    display: 'inline-block',
                }}
            >
                {props.children}
            </AntdButton>
        );
    } else if ('onClick' in props) {
        return (
            <AntdButton
                onClick={props.onClick}
                className={className}
                style={style}
                data-testid={testID}
            >
                {props.children}
            </AntdButton>
        );
    } else {
        return <AntdButton>{props.children}</AntdButton>;
    }
};

export const BaseButton = styled(BaseButtonComponent)<StyledButtonProps>(
    ({ borderColor = 'transparent' }) => ({
        border: '2px solid',
        borderColor,
    }),
    ({ withshadow }) => ({
        '&&': {
            height: 'auto',
            lineHeight: 1,
            transition: '0.2s background-color, 0.2s border-color, 0.2s color',
            boxShadow: withshadow ? '0 8px 12px 0 rgba(183, 186, 190, 0.5)' : 'none',
            '&:hover': {
                boxShadow: withshadow ? '0 8px 12px 0 rgba(183, 186, 190, 0.5)' : 'none',
            },
        },
    }),
    ({ issmall, isfullwidth, fontSize }) =>
        issmall
            ? {
                  ...font.heavy(fontSize || font.size.basePlus),
                  // Antd uses !important to force a 0.1px padding for anchor buttons due to a Chrome bug,
                  // this forces us to also use important.
                  // Reference: https://github.com/ant-design/ant-design/blob/9275866f24151072ef3feb9ece150ece0f04cf2a/components/button/style/index.less#L239
                  paddingTop: '0.61rem !important',
                  padding: `0.61rem ${isfullwidth ? 0 : '1.75rem'} 0.61rem`,
                  [mediaQueries.xs]: {
                      padding: `0.61rem ${isfullwidth ? 0 : '0.88rem'} 0.61rem`,
                  },
                  width: 'auto',
                  borderRadius: 4,
              }
            : {
                  ...font.heavy(fontSize || font.size.medium),
                  // !important is used to override an Antd !important usage
                  paddingTop: '0.88rem !important',
                  padding: `0.88rem ${isfullwidth ? 0 : '5.25rem'} 0.88rem`,
                  width: 'auto',
                  borderRadius: '0.35rem',
                  [mediaQueries.sm]: {
                      // !important is used to override an Antd !important usage
                      paddingTop: '0.88rem !important',
                      padding: `0.88rem ${isfullwidth ? 0 : '2.63rem'} 0.88rem`,
                  },
                  [mediaQueries.xs]: {
                      padding: `0.88rem ${isfullwidth ? 0 : '0.88rem'} 0.88rem`,
                  },
              },
    ({ isPill }) =>
        isPill
            ? {
                  borderRadius: 25,
              }
            : {},
    ({ horizontalPadding }) => ({
        textAlign: 'center',
        ...(horizontalPadding == 'large'
            ? {
                  paddingLeft: '5.25rem',
                  paddingRight: '5.25rem',
              }
            : horizontalPadding == 'medium'
            ? {
                  paddingLeft: '1.75rem',
                  paddingRight: '1.75rem',
              }
            : horizontalPadding == 'small'
            ? {
                  paddingLeft: '1.31rem',
                  paddingRight: '1.31rem',
              }
            : horizontalPadding == 'no-padding'
            ? {
                  paddingLeft: 0,
                  paddingRight: 0,
              }
            : {}),
    }),
    ({ width }) => {
        if (width === 'normal') {
            return {
                minWidth: 241,
                width: 'fit-content',
            };
        }
        return {};
    },
);

const AcceptButton = styled(BaseButton)({
    '&&': {
        backgroundColor: color.royalBlue,
        color: color.white,

        '&:hover': {
            backgroundColor: color.denim,
        },
    },
});

const NoBackgroundButton = styled(BaseButton)({
    '&&': {
        backgroundColor: 'transparent',
        color: color.steelGray,
    },
});

const WhiteButton = styled(BaseButton)({
    '&&': {
        backgroundColor: color.white,
        color: color.pickledBluewood,
        border: `2px solid ${color.linkWaterLight}`,
    },
});

const White2Button = styled(BaseButton)({
    '&&': {
        backgroundColor: color.white,
        color: color.ebonyClay,
        border: '1.5px solid #E9EEF4',

        '&:hover': {
            backgroundColor: color.white,
        },
    },
});

const CancelButton = styled(BaseButton)({
    '&&': {
        backgroundColor: hexToRgba(color.casper, 0.45),
        color: color.steelGray,

        '&:hover': {
            backgroundColor: hexToRgba(color.cadetBlue, 0.45),
        },
    },
});

const LightButton = styled(BaseButton)({
    '&&': {
        backgroundColor: color.scooter,
        color: color.white,

        '&:hover': {
            backgroundColor: color.easternBlue,
        },
    },
});

const ButtonWithIcon = styled(BaseButton)({
    '&&': {
        backgroundColor: '#2d80e226',
        color: color.royalBlue,

        '&:hover': {
            backgroundColor: '#2d80e240',
        },
    },
});

const getCustomStylesForDisabled = (type?: ButtonType) => {
    switch (type) {
        case 'accept': {
            return {
                '&&': {
                    backgroundColor: color.royalBlue,
                    borderColor: color.royalBlue,
                    color: color.white,
                    cursor: 'not-allowed',
                    opacity: 0.4,

                    '&:hover': {
                        backgroundColor: color.royalBlue,
                        color: color.white,
                        borderColor: color.royalBlue,
                    },
                },
            };
        }
        case 'accept2': {
            return {
                '&&': {
                    backgroundColor: color.casper,
                    borderColor: color.casper,
                    color: color.white,
                    cursor: 'not-allowed',

                    '&:hover': {
                        backgroundColor: color.gullGray,
                        color: color.white,
                        borderColor: color.gullGray,
                    },
                },
            };
        }
        default:
            return {};
    }
};

const DisabledButton = styled(BaseButton)<StyledButtonProps>(props => {
    const customStyles = getCustomStylesForDisabled(props.buttontype);

    return {
        '&&': {
            backgroundColor: color.casper,
            color: color.white,
            cursor: 'not-allowed',

            '&:hover': {
                backgroundColor: color.casper,
                color: color.white,
                borderColor: color.casper,
            },
        },
        ...customStyles,
    };
});

const buttons = {
    accept: AcceptButton,
    accept2: AcceptButton,
    cancel: CancelButton,
    light: LightButton,
    disabled: DisabledButton,
    noBackground: NoBackgroundButton,
    white: WhiteButton,
    white2: White2Button,
    withIcon: ButtonWithIcon,
};

interface ButtonProps {
    type?: ButtonType;
    withShadow?: boolean;
    noHorizontalPadding?: boolean;
    small?: boolean;
    fullWidth?: boolean;
    disabled?: boolean;
    disabledTooltip?: string;
    widthPercentage?: number;
    loading?: boolean;
    marginTop?: number;
    marginLeft?: number;
    marginRight?: number;
    centered?: boolean;
    testID?: string;
    fontSize?: number;
    horizontalPadding?: PaddingSize;
    className?: string;
    width?: ButtonWidth;
    /**
     * Makes the button background appear rounded.
     * (Applies a 25px border radius to the button.)
     */
    rounded?: boolean;

    onClick?: () => void;
    to?: History.LocationDescriptor;
    replace?: boolean;
    href?: string;
    target?: string;
}

interface ClickableButtonProps extends ButtonProps {
    onClick: () => void;
}

interface LinkButtonProps extends ButtonProps {
    to: History.LocationDescriptor;
    replace?: boolean;
}

interface AnchorButtonProps extends ButtonProps {
    href: string;
    target?: '_blank' | string;
}

// Not using 'disabled' as a prop, because it causes trouble with antd Tooltip triggering
export class Button extends React.PureComponent<
    ClickableButtonProps | LinkButtonProps | AnchorButtonProps
> {
    private handleOnClick = () => {
        if (!this.props.disabled && typeof this.props.onClick === 'function') {
            this.props.onClick();
        }
    };

    public render(): JSX.Element {
        const {
            type = 'accept',
            small = false,
            fullWidth,
            disabled,
            disabledTooltip,
            children,
            horizontalPadding = 'large',
            noHorizontalPadding,
            withShadow,
            loading,
            widthPercentage = 'inherit',
            marginTop = 0,
            marginLeft = 'initial',
            marginRight = 'initial',
            centered,
            testID = '',
            fontSize,
            className = '',
            rounded = false,
            width,
            ...linkProps
        } = this.props;

        const ButtonComponent = buttons[disabled ? 'disabled' : type];

        return (
            <Tooltip title={disabledTooltip}>
                <ButtonComponent
                    {...linkProps}
                    // 'to' or 'href' will override any click handler.
                    onClick={this.handleOnClick}
                    isfullwidth={fullWidth ? 1 : 0}
                    issmall={small ? 1 : 0}
                    horizontalPadding={noHorizontalPadding ? 'no-padding' : horizontalPadding}
                    buttontype={type}
                    fontSize={fontSize}
                    withshadow={withShadow ? 1 : 0}
                    isPill={rounded}
                    style={{
                        marginTop,
                        width: fullWidth ? '100%' : `${widthPercentage}%`,
                        marginLeft: centered ? 'auto' : marginLeft,
                        marginRight: centered ? 'auto' : marginRight,
                        display: centered ? 'block' : 'initial',
                    }}
                    testID={testID}
                    width={width}
                    className={className}
                >
                    {loading ? (
                        <Spin
                            indicator={
                                <LoadingIcon
                                    color={type === 'white' ? color.royalBlue : color.white}
                                    type="loading"
                                    spin
                                />
                            }
                        />
                    ) : (
                        children
                    )}
                </ButtonComponent>
            </Tooltip>
        );
    }
}
